(() => {
  'use strict';

  const focusableSelector = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class NexoraHeader {
    constructor(root) {
      root.dataset.nxReady = 'true';
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.root = root;
      this.wrap = root.closest('[data-nx-header-wrap]');
      const drawerId = root.dataset.nxDrawerId;
      this.drawer = (drawerId && document.getElementById(drawerId)) || root.querySelector('[data-nx-drawer]') || document.querySelector('[data-nx-drawer]');
      this.drawerPanel = root.querySelector('[data-nx-drawer-panel]');
      this.openButton = root.querySelector('[data-nx-drawer-open]');
      this.closeButtons = [...(this.drawer?.querySelectorAll('[data-nx-drawer-close]') || [])];
      this.nav = root.querySelector('[data-nx-primary-nav]');
      this.menu = root.querySelector('[data-nx-menu-items]');
      this.more = root.querySelector('[data-nx-more]');
      this.overflow = root.querySelector('[data-nx-overflow-list]');
      this.lastFocused = null;
      this.resizeTimer = null;
      this.boundKeydown = this.onDocumentKeydown.bind(this);
      this.init();
    }

    setDetailsState(details, open) {
      if (!(details instanceof HTMLDetailsElement)) return;
      if (open) details.setAttribute('open', '');
      else details.removeAttribute('open');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    init() {
      this.root.querySelectorAll('details > summary').forEach((summary) => summary.setAttribute('aria-expanded', summary.parentElement.open ? 'true' : 'false'));
      this.openButton?.addEventListener('click', () => this.openDrawer(), { signal: this.signal });
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeDrawer(), { signal: this.signal }));
      this.root.addEventListener('toggle', (event) => this.onDetailsToggle(event), { capture: true, signal: this.signal });
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.reflowNavigation(), 80);
        if (window.innerWidth > 1199 && this.drawer?.classList.contains('is-open')) this.closeDrawer(false);
      }, { passive: true, signal: this.signal });
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => this.reflowNavigation(), 80);
        });
        this.resizeObserver.observe(this.root);
      }
      window.addEventListener('scroll', () => {
        this.root.classList.toggle('is-scrolled', window.scrollY > 12);
      }, { passive: true, signal: this.signal });
      document.addEventListener('keydown', this.boundKeydown, { signal: this.signal });
      requestAnimationFrame(() => this.reflowNavigation());
      if (document.fonts?.ready) document.fonts.ready.then(() => this.reflowNavigation());
    }

    openDrawer() {
      if (!this.drawer) return;
      this.lastFocused = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('nx-drawer-locked');
      requestAnimationFrame(() => {
        const first = this.drawerPanel?.querySelector(focusableSelector);
        (first || this.drawerPanel)?.focus();
      });
    }

    closeDrawer(restoreFocus = true) {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.openButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('nx-drawer-locked');
      this.drawer.querySelectorAll('details[open]').forEach((details) => this.setDetailsState(details, false));
      if (restoreFocus && this.lastFocused instanceof HTMLElement) this.lastFocused.focus();
    }

    trapFocus(event) {
      if (!this.drawer?.classList.contains('is-open') || event.key !== 'Tab') return;
      const focusables = [...this.drawerPanel.querySelectorAll(focusableSelector)].filter((node) => node.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        if (this.drawer?.classList.contains('is-open')) {
          event.preventDefault();
          this.closeDrawer();
          return;
        }
        const opened = this.root.querySelector('details[open]');
        if (opened) {
          this.setDetailsState(opened, false);
          opened.querySelector('summary')?.focus();
        }
      }
      this.trapFocus(event);
    }

    onDetailsToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      if (!details.open) return;
      const group = details.closest('.nx-header__nav, .nx-header__drawer-panel');
      group?.querySelectorAll('details[open]').forEach((other) => {
        if (other !== details && !details.contains(other) && !other.contains(details)) this.setDetailsState(other, false);
      });
      const panel = details.querySelector(':scope > [data-nx-menu-panel], :scope > .nx-header__more-list');
      if (!panel) return;
      panel.classList.remove('is-align-end', 'is-open-left');
      requestAnimationFrame(() => {
        const rect = panel.getBoundingClientRect();
        if (rect.right > window.innerWidth - 12) {
          if (panel.classList.contains('nx-menu-tree__panel')) panel.classList.add('is-open-left');
          else panel.classList.add('is-align-end');
        }
      });
    }

    resetOverflow() {
      if (!this.menu || !this.overflow || !this.more) return;
      const moved = [...this.overflow.children];
      moved.sort((a, b) => Number(a.dataset.nxOrder) - Number(b.dataset.nxOrder));
      moved.forEach((item) => this.menu.appendChild(item));
      this.more.hidden = true;
      this.more.removeAttribute('open');
    }

    reflowNavigation() {
      if (!this.nav || !this.menu || !this.more || !this.overflow) return;
      if (this.root.dataset.nxLayout === 'minimal' || window.innerWidth <= 1199) {
        this.resetOverflow();
        return;
      }
      [...this.menu.children].forEach((item, index) => {
        if (!item.dataset.nxOrder) item.dataset.nxOrder = String(index);
      });
      this.resetOverflow();
      const items = () => [...this.menu.querySelectorAll(':scope > [data-nx-nav-item]')];
      const fits = () => this.menu.scrollWidth <= this.nav.clientWidth - (this.more.hidden ? 0 : this.more.offsetWidth + 12);
      if (fits()) return;
      this.more.hidden = false;
      let list = items();
      while (list.length > 1 && !fits()) {
        const item = list.pop();
        this.overflow.prepend(item);
        list = items();
      }
      if (!this.overflow.children.length) this.more.hidden = true;
    }

    destroy() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      clearTimeout(this.resizeTimer);
      document.body.classList.remove('nx-drawer-locked');
      this.root.dataset.nxReady = 'false';
    }
  }

  const instances = new WeakMap();
  const init = (scope = document) => {
    scope.querySelectorAll?.('[data-nx-header]').forEach((root) => {
      if (root.dataset.nxReady === 'true') return;
      const instance = new NexoraHeader(root);
      instances.set(root, instance);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.('[data-nx-header]');
    instances.get(root)?.destroy();
  });
})();
